import { followingSidebar } from '../../utils/sidebar.utils.js';
import { clearJobsFilterQueryParameters, updateJobsSearchQuery } from '../../utils/jobs-search-query.utils.js';

const equalizersRegistry = new WeakMap();
const rowTopTolerance = 4;
const equalizedSelectors = [
  '.job-card__meta',
  '.job-card__title',
  '.job-card__details',
];

const getSearchJobCards = (grid) => {
  return Array.from(grid.querySelectorAll('.job-card'));
};

const getSearchJobCardRows = (grid) => {
  const cards = getSearchJobCards(grid);
  const rows = [];

  cards.forEach((card) => {
    const top = card.getBoundingClientRect().top;
    const existingRow = rows.find((row) => {
      return Math.abs(row.top - top) <= rowTopTolerance;
    });

    if (existingRow) {
      existingRow.cards.push(card);
      existingRow.top = Math.min(existingRow.top, top);
      return;
    }

    rows.push({
      top,
      cards: [card],
    });
  });

  return rows
    .sort((a, b) => a.top - b.top)
    .map((row) => row.cards);
};

const getSearchJobCardEqualizerGroups = (grid) => {
  const rows = getSearchJobCardRows(grid);
  const groups = [];

  rows.forEach((row) => {
    equalizedSelectors.forEach((selector) => {
      const elements = row
        .map((card) => card.querySelector(selector))
        .filter(Boolean);

      if (elements.length) {
        groups.push(elements);
      }
    });
  });

  return groups;
};

const setupSearchJobCardsEqualizer = (grid) => {
  const equalizer = equalizersRegistry.get(grid);

  if (equalizer) {
    equalizer.refresh();
    return;
  }

  if (typeof window.lineTitlesFunc !== 'function') {
    return;
  }

  equalizersRegistry.set(
    grid,
    window.lineTitlesFunc([], {
      getGroups: () => getSearchJobCardEqualizerGroups(grid),
    })
  );
};

const refreshSearchJobCardsEqualizer = (scope = document) => {
  const root = scope instanceof Element ? scope : document;
  const grids = root.matches?.('.search-sec__result-grid')
    ? [root]
    : Array.from(root.querySelectorAll('.search-sec__result-grid'));

  grids.forEach((grid) => {
    if (!getSearchJobCards(grid).length) {
      return;
    }

    setupSearchJobCardsEqualizer(grid);
  });

  window.refreshFollowingSidebar?.();
};

const initializedSearchSections = new WeakSet();

const scrollToJobSearchResults = () => {
  const result = document.querySelector('.search-sec #content-sticky');

  if (!result) {
    return;
  }

  const headerOffset = document.querySelector('.header')?.offsetHeight || 0;
  const scrollOffset = headerOffset + 20;
  const top = result.getBoundingClientRect().top + window.scrollY - scrollOffset;

  window.scrollTo({
    top: Math.max(0, top),
    behavior: 'smooth',
  });
};

const initSearchSections = (scope = document) => {
  const root = scope instanceof Element ? scope : document;
  const sections = root.matches?.('.search-sec')
    ? [root]
    : Array.from(root.querySelectorAll('.search-sec'));

  sections.forEach((searchSection) => {
    if (initializedSearchSections.has(searchSection)) {
      return;
    }

    initializedSearchSections.add(searchSection);

    const sidebar = searchSection.querySelector('#sidebar');
    const contentSticky = searchSection.querySelector('#content-sticky');
    const filtersBtn = searchSection.querySelector('#filters-btn');
    const contentResult = searchSection.querySelector('#content-result');
    const form = searchSection.querySelector('[data-jobs-search-form]');
    const clearFilters = searchSection.querySelector('[data-jobs-clear-filters]');
    const isColumnTypeSec = searchSection.classList.contains('column-type');
    const searchSidebarDesktopBreakpoint = 902;
    let followingSidebarInstance = null;
    let searchTimer = null;
    const searchDebounceDelay = 450;

    const updateFollowingSidebar = () => {
      const shouldFollowSidebar =
        sidebar &&
        contentSticky &&
        window.innerWidth >= searchSidebarDesktopBreakpoint &&
        !isColumnTypeSec;

      if (!shouldFollowSidebar) {
        followingSidebarInstance?.destroy();
        followingSidebarInstance = null;
        return;
      }

      followingSidebarInstance ??= followingSidebar({
        sidebar,
        contentSticky,
        gap: 130,
        bottomGap: 40,
      });
      followingSidebarInstance?.refresh();
    };

    const refreshFollowingSidebar = () => {
      followingSidebarInstance?.refresh();
    };

    const refreshFollowingSidebarConstraint = () => {
      followingSidebarInstance?.refreshConstraint();
    };

    const accordionResizeTransitions = new Map();
    const accordionResizeTransitionTimeout = 400;

    const endAccordionResizeTransition = (content) => {
      const timeout = accordionResizeTransitions.get(content);

      if (timeout === undefined) return;

      window.clearTimeout(timeout);
      accordionResizeTransitions.delete(content);

      if (!accordionResizeTransitions.size) {
        followingSidebarInstance?.endResizeTransition();
      }
    };

    const beginAccordionResizeTransition = (content) => {
      if (!content || !followingSidebarInstance) return;

      const previousTimeout = accordionResizeTransitions.get(content);

      if (previousTimeout !== undefined) {
        window.clearTimeout(previousTimeout);
      } else if (!accordionResizeTransitions.size) {
        followingSidebarInstance.beginResizeTransition();
      }

      const timeout = window.setTimeout(
        () => endAccordionResizeTransition(content),
        accordionResizeTransitionTimeout
      );

      accordionResizeTransitions.set(content, timeout);
    };

    if (sidebar && contentSticky) {
      updateFollowingSidebar();
      window.addEventListener('resize', updateFollowingSidebar, { passive: true });
    }

    const submitSearch = (page = 1, { scroll = false } = {}) => {
      if (!form || typeof window.load !== 'function') {
        return;
      }

      if (window.history?.replaceState) {
        const url = updateJobsSearchQuery(new URL(window.location.href), new FormData(form), form.dataset.jobsInternal);
        if (page > 1) url.searchParams.set('page', page);
        window.history.replaceState(window.history.state, '', url);
      }

      window.load(
        form.dataset.jobsSearchUrl,
        `form:#${form.id}`,
        `page=${page}`,
        `internal=${form.dataset.jobsInternal || '0'}`,
        `scroll=${scroll ? 1 : 0}`
      );
    };

    const scheduleSearch = ({ scroll = true } = {}) => {
      window.clearTimeout(searchTimer);
      searchTimer = window.setTimeout(() => submitSearch(1, { scroll }), searchDebounceDelay);
    };

    const clearFilterQueryParameters = () => {
      if (!window.history?.replaceState) {
        return;
      }

      const url = new URL(window.location.href);
      clearJobsFilterQueryParameters(url);
      window.history.replaceState(window.history.state, '', url);
    };

    if (filtersBtn && sidebar && contentSticky && contentResult) {
      const closeMobileFilters = () => {
        filtersBtn.classList.remove('is-open-filters');
        sidebar.classList.remove('is-open-filters');
        contentSticky.classList.remove('is-open-filters');
        contentResult.classList.remove('is-open-filters');
      };

      filtersBtn.addEventListener('click', () => {
        filtersBtn.classList.toggle('is-open-filters');
        sidebar.classList.toggle('is-open-filters');
        contentSticky.classList.toggle('is-open-filters');
        contentResult.classList.toggle('is-open-filters');
      });

      contentResult.addEventListener('click', () => {
        if (contentResult.classList.contains('is-open-filters')) {
          closeMobileFilters();
        }
      });

      contentSticky.addEventListener('click', (event) => {
        if (!contentSticky.classList.contains('is-open-filters')) {
          return;
        }

        if (sidebar.contains(event.target) || filtersBtn.contains(event.target)) {
          return;
        }

        closeMobileFilters();
      });
    }

    if (form) {
      searchSection.addEventListener('change', (event) => {
        if (event.target.getAttribute('form') === form.id) {
          if (event.target.matches('select[multiple]')) {
            scheduleSearch({ scroll: false });
            return;
          }

          submitSearch(1, { scroll: true });
        }
      });
      searchSection.addEventListener('range:change', () => submitSearch(1, { scroll: true }));
      searchSection.addEventListener('input', (event) => {
        if (event.target.getAttribute('form') === form.id
          && event.target.matches('input[type="text"], input[type="number"]')) {
          scheduleSearch();
        }
      });
    }

    if (clearFilters && form) {
      clearFilters.addEventListener('click', () => {
        window.clearTimeout(searchTimer);
        const associatedControls = searchSection.querySelectorAll(
          `[form="${form.id}"]`
        );

        associatedControls.forEach((control) => {
          if (control.matches('input[type="checkbox"], input[type="radio"]')) {
            control.checked = false;
            return;
          }

          if (control.matches('select[multiple]')) {
            Array.from(control.options).forEach((option) => {
              option.selected = false;
            });
            window.updateMetaSelect?.(control.id);
            return;
          }

          if (control.matches('input, select, textarea')) {
            control.value = '';
            if (control.matches('select')) {
              window.updateMetaSelect?.(control.id);
            }
          }
        });

        clearFilterQueryParameters();
        submitSearch(1, { scroll: true });
        refreshFollowingSidebar();
      });
    }

    searchSection.querySelectorAll('.search-sec__sidebar-item-toggle').forEach((toggle) => {
      const toggleItem = () => {
        const isOpen = toggle.classList.contains('is-active');

        toggle.classList.toggle('is-active', !isOpen);
        toggle.classList.toggle('is-closed', isOpen);
        beginAccordionResizeTransition(toggle.nextElementSibling);
        refreshFollowingSidebarConstraint();
      };

      toggle.addEventListener('click', toggleItem);
    });

    const finishAccordionResizeTransition = (event) => {
      if (
        event.propertyName === 'max-height' &&
        event.target.classList.contains('search-sec__sidebar-item-content')
      ) {
        endAccordionResizeTransition(event.target);
      }
    };

    searchSection.addEventListener('transitionend', finishAccordionResizeTransition);
    searchSection.addEventListener('transitioncancel', finishAccordionResizeTransition);
  });
};

window.refreshSearchJobCardsEqualizer = refreshSearchJobCardsEqualizer;
window.initSearchJobCardsEqualizer = refreshSearchJobCardsEqualizer;
window.scrollToJobSearchResults = scrollToJobSearchResults;
window.initSearchSections = initSearchSections;

initSearchSections();
refreshSearchJobCardsEqualizer();

/*if (document.getElementsByClassName('job-search-section__form')[0]) {
  const checkboxesTypes = document.querySelectorAll(
    'input[type="checkbox"][name="type[]"], input[type="checkbox"][name="type_second[]"]'
  );

  function syncCheckboxes(event) {
    const targetCheckbox = event.target;
    const isChecked = targetCheckbox.checked;
    const targetValue = targetCheckbox.value;

    checkboxesTypes.forEach((checkbox) => {
      if (checkbox.value === targetValue) {
        checkbox.checked = isChecked;
      }
    });
  }

  checkboxesTypes.forEach((checkbox) => {
    checkbox.addEventListener('change', syncCheckboxes);
  });

  const checkboxesSectors = document.querySelectorAll(
    'input[type="checkbox"][name="sector[]"], input[type="checkbox"][name="sector_second[]"]'
  );

  function syncCheckboxesSectors(event) {
    const targetCheckbox = event.target;
    const isChecked = targetCheckbox.checked;
    const targetValue = targetCheckbox.value;

    checkboxesSectors.forEach((checkbox) => {
      if (checkbox.value === targetValue) {
        checkbox.checked = isChecked;
      }
    });
  }

  checkboxesSectors.forEach((checkbox) => {
    checkbox.addEventListener('change', syncCheckboxesSectors);
  });
}*/
